import type { CurrencyCode } from '../types/region';

// Country landing pages (/us, /uk, /au, /ca, /ae). Everything here must be true
// for a coach or gym in that country today: prices are billed in USD (local
// figures are indicative), clients pay the coach on the coach's own Stripe /
// PayPal / Wise link, and data sits in India (database, API) + Australia (files).
// No customer counts, logos or testimonials: we have none to cite per country yet.

export type CountryCode = 'us' | 'uk' | 'au' | 'ca' | 'ae';

export interface CountryPage {
  code: CountryCode;
  path: string;
  hrefLang: string;
  name: string; // "the UK"
  adjective: string; // "UK"
  currency: CurrencyCode;
  spelling: 'us' | 'uk';
  title: string;
  description: string;
  keywords: string;
  eyebrow: string;
  h1: string;
  intro: string;
  /** Competitors coaches in this market most often compare us with (slugs in CLIENT_LADDER / alternatives). */
  competitors: string[];
  points: { title: string; body: string }[];
  /** Gulf page leads with gyms. */
  gymFirst?: boolean;
  faq: { q: string; a: string }[];
}

const PAY_YOUR_OWN_LINK =
  'Clients pay you on your own Stripe, PayPal or Wise payment link, then tap "I’ve paid" and you confirm. The money goes straight to you and Trainzilla takes no commission.';

export const COUNTRY_PAGES: CountryPage[] = [
  {
    code: 'us',
    path: '/us',
    hrefLang: 'en-US',
    name: 'the US',
    adjective: 'US',
    currency: 'USD',
    spelling: 'us',
    title: 'Personal Trainer Software for US Coaches, $19/mo Unlimited | TrainZilla',
    description:
      'Coaching software for US personal trainers and online coaches: AI-drafted workout and nutrition plans you approve, branded client app, lbs support and your own payment link. $19/month, unlimited clients.',
    keywords:
      'personal trainer software usa, online coaching app for personal trainers, trainerize alternative, truecoach alternative, fitness coaching software united states',
    eyebrow: 'For coaches in the United States',
    h1: 'Personal trainer software for US coaches: $19 a month, unlimited clients',
    intro:
      'Most US coaching apps charge more as your roster grows. Trainzilla Coach Pro is one flat $19/month for unlimited clients, with branding, nutrition, payments and an AI that drafts next week’s plans for you to approve.',
    competitors: ['everfit', 'trainheroic', 'hevy-coach', 'coachrx'],
    points: [
      { title: 'Priced in dollars, not per client', body: 'Coach Pro is $19/month or $190/year, billed in US dollars, with no per-client fees and no setup fee for branding.' },
      { title: 'Get paid your way', body: PAY_YOUR_OWN_LINK },
      { title: 'Pounds, inches and your time zone', body: 'Clients log weight in lbs or kg, sessions follow your working hours in your own time zone, and Apple Health sync works on iPhone.' },
      { title: 'Switch without starting over', body: 'Invite clients in bulk from a CSV or Excel export and book a free move-over call with the founder at a time that suits your time zone.' },
    ],
    faq: [
      { q: 'Is Trainzilla available in the US?', a: 'Yes. Coaches and clients anywhere in the US can sign up. Coach Pro is billed at $19/month or $190/year in US dollars, through the web, the App Store or Google Play.' },
      { q: 'How do my US clients pay me?', a: PAY_YOUR_OWN_LINK + ' You set your plan prices in US dollars.' },
      { q: 'Where is my data stored?', a: 'The database and application servers are in India and uploaded files are stored on AWS in Sydney, Australia. Our privacy policy lists every provider and where it processes data.' },
      { q: 'Can I try it before paying?', a: 'Yes. The 30-day free trial covers your first client and needs no card.' },
    ],
  },
  {
    code: 'uk',
    path: '/uk',
    hrefLang: 'en-GB',
    name: 'the UK',
    adjective: 'UK',
    currency: 'GBP',
    spelling: 'uk',
    title: 'Personal Trainer Software UK, about £15/mo Unlimited Clients | TrainZilla',
    description:
      'Online coaching software for UK personal trainers: AI-drafted training and nutrition plans you approve, your branding in the client app, GDPR-ready privacy policy. $19/month (about £15), unlimited clients.',
    keywords:
      'personal trainer software uk, online pt software uk, my pt hub alternative, pt distinction alternative, kahunas alternative, online coaching app uk',
    eyebrow: 'For personal trainers in the UK',
    h1: 'Personal trainer software for UK coaches: about £15 a month, unlimited clients',
    intro:
      'PT Distinction, My PT Hub and Kahunas all price by client numbers or tier. Trainzilla Coach Pro is a flat $19/month (about £15) for unlimited clients, with your branding, nutrition, payments and an AI that drafts next week’s programme for you to approve.',
    competitors: ['pt-distinction', 'my-pt-hub', 'kahunas', 'hubfit'],
    points: [
      { title: 'One price, whatever your roster', body: 'Coach Pro is $19/month (about £15) or $190/year, billed in US dollars. Branding, nutrition, payments and the AI plan agent are included, not add-ons.' },
      { title: 'Get paid your way', body: PAY_YOUR_OWN_LINK },
      { title: 'GDPR and UK GDPR', body: 'Our privacy policy covers UK GDPR: legal bases, every sub-processor, Standard Contractual Clauses with the UK Addendum for transfers, and a Data Processing Agreement on request. EU/UK visitors get a cookie consent banner, and accounts can be fully deleted.' },
      { title: 'kg or lbs, plus body composition', body: 'Clients log weight in kg or lbs, track body composition from InBody or DEXA reports, and sync smart scales through Apple Health or Health Connect.' },
    ],
    faq: [
      { q: 'Can I pay in pounds?', a: 'Coach Pro is billed in US dollars: $19/month, which is about £15 at current rates. Your bank or card converts it. Your own clients pay you on your own payment link, in whatever currency that link uses.' },
      { q: 'Is Trainzilla GDPR compliant for UK coaches?', a: 'Our privacy policy is written for UK GDPR as well as EU GDPR and India’s DPDP Act. For your clients’ data you are the controller and we act as your processor; email support@trainzilla.in for a Data Processing Agreement with the UK Addendum.' },
      { q: 'Where is my clients’ data stored?', a: 'The database and application servers are in India and uploaded files are stored on AWS in Sydney, Australia. There is no UK or EU data region today.' },
      { q: 'Can I move my clients from PT Distinction or My PT Hub?', a: 'Yes. Export your client list, invite everyone in bulk from a CSV or Excel file, and book a free move-over call with the founder. Plans and templates are rebuilt in Trainzilla, where the AI can draft them from each client’s intake.' },
    ],
  },
  {
    code: 'au',
    path: '/au',
    hrefLang: 'en-AU',
    name: 'Australia',
    adjective: 'Australian',
    currency: 'AUD',
    spelling: 'uk',
    title: 'Personal Trainer Software Australia, about A$29/mo Unlimited | TrainZilla',
    description:
      'Coaching software for Australian personal trainers: AI-drafted training and nutrition plans you approve, branded client app, files stored in Sydney. $19 USD/month (about A$29), unlimited clients.',
    keywords:
      'personal trainer software australia, online coaching app australia, pt software australia, everfit alternative, hubfit alternative',
    eyebrow: 'For personal trainers in Australia',
    h1: 'Personal trainer software for Australian coaches: about A$29 a month, unlimited clients',
    intro:
      'Trainzilla Coach Pro is a flat $19 USD a month (about A$29) for unlimited clients, with your branding, nutrition, payments and an AI that drafts next week’s programme for you to approve.',
    competitors: ['everfit', 'hubfit', 'kahunas', 'pt-distinction'],
    points: [
      { title: 'Flat price, unlimited clients', body: 'Coach Pro is $19 USD/month (about A$29) or $190/year. No per-client fees and no setup fee for branding.' },
      { title: 'Get paid your way', body: PAY_YOUR_OWN_LINK },
      { title: 'Files stored in Sydney', body: 'Progress photos, videos and documents your clients upload are stored on AWS in Sydney. The database and application servers are in India.' },
      { title: 'Built for hybrid coaching', body: 'Run in-person sessions on split working hours, sell session packs, and coach online clients in the same app.' },
    ],
    faq: [
      { q: 'Is the price in Australian dollars?', a: 'Coach Pro is billed in US dollars: $19/month, which is about A$29 at current rates. Your bank or card converts it.' },
      { q: 'How do my clients pay me?', a: PAY_YOUR_OWN_LINK },
      { q: 'Where is data stored?', a: 'Uploaded files are on AWS in Sydney. The database and application servers are in India. Our privacy policy lists every provider.' },
      { q: 'Is there a free trial?', a: 'Yes. 30 days with your first client, no card needed.' },
    ],
  },
  {
    code: 'ca',
    path: '/ca',
    hrefLang: 'en-CA',
    name: 'Canada',
    adjective: 'Canadian',
    currency: 'CAD',
    spelling: 'us',
    title: 'Personal Trainer Software Canada, about C$26/mo Unlimited | TrainZilla',
    description:
      'Coaching software for Canadian personal trainers and online coaches: AI-drafted plans you approve, branded client app, kg or lbs. $19 USD/month (about C$26), unlimited clients.',
    keywords:
      'personal trainer software canada, online coaching app canada, trainerize alternative canada, fitness coaching software canada',
    eyebrow: 'For coaches in Canada',
    h1: 'Personal trainer software for Canadian coaches: about C$26 a month, unlimited clients',
    intro:
      'Trainzilla Coach Pro is a flat $19 USD a month (about C$26) for unlimited clients, with branding, nutrition, payments and an AI that drafts next week’s plans for you to approve.',
    competitors: ['everfit', 'trainheroic', 'coachrx', 'pt-distinction'],
    points: [
      { title: 'Flat price, unlimited clients', body: 'Coach Pro is $19 USD/month (about C$26) or $190/year, with branding, nutrition and payments included.' },
      { title: 'Get paid your way', body: PAY_YOUR_OWN_LINK },
      { title: 'kg or lbs, your call', body: 'Each client picks kilograms or pounds. Apple Health and Health Connect bring in smart-scale and body data.' },
      { title: 'Switch without starting over', body: 'Bulk-invite clients from CSV or Excel and book a free move-over call with the founder in your time zone.' },
    ],
    faq: [
      { q: 'Is the price in Canadian dollars?', a: 'Coach Pro is billed in US dollars: $19/month, which is about C$26 at current rates.' },
      { q: 'How do my clients pay me?', a: PAY_YOUR_OWN_LINK },
      { q: 'Where is data stored?', a: 'The database and application servers are in India and uploaded files are on AWS in Sydney, Australia.' },
      { q: 'Does it work in French?', a: 'The client app can be switched to French, along with 10 other languages.' },
    ],
  },
  {
    code: 'ae',
    path: '/ae',
    hrefLang: 'en-AE',
    name: 'the UAE',
    adjective: 'UAE',
    currency: 'AED',
    spelling: 'uk',
    gymFirst: true,
    title: 'Gym & PT Software in the UAE, Memberships to Renewals | TrainZilla',
    description:
      'Gym and personal trainer software for the UAE and Gulf: memberships, face and QR check-in, group classes, renewals and desk payments, plus an Arabic-ready member app. From $49/month (about AED 180).',
    keywords:
      'gym management software uae, gym software dubai, personal trainer software uae, fitness studio software gulf, gym membership software saudi arabia',
    eyebrow: 'For gyms and coaches in the UAE and Gulf',
    h1: 'Gym and personal trainer software for the UAE: memberships, renewals and PT in one app',
    intro:
      'Run memberships, check-ins, classes and renewals, and give members an app with your gym’s logo, available in Arabic and English. Gym plans start at $49/month (about AED 180) for 5 coach seats; solo coaches pay $19/month (about AED 70) for unlimited clients.',
    competitors: ['everfit', 'kahunas', 'pt-distinction', 'hubfit'],
    points: [
      { title: 'Front desk without the register', body: 'Members check in with their face at a tablet kiosk or show a QR code from their app. Record cash or card desk payments with a photo of the receipt.' },
      { title: 'Renewals you can see coming', body: 'The owner dashboard lists memberships due this week and month, which will auto-renew and which have lapsed, alongside revenue by coach and location.' },
      { title: 'Classes and PT together', body: 'Recurring group classes with capacity, waitlist and attendance, plus personal training plans, session packs and nutrition for PT clients.' },
      { title: 'Arabic and English', body: 'Members can switch the app to Arabic. Your gym’s logo and colours appear throughout, and your gym page can run on your own domain.' },
    ],
    faq: [
      { q: 'What does it cost in dirhams?', a: 'Gym Starter is $49/month (about AED 180) for up to 5 coach seats and Gym Pro is $99/month (about AED 364) for 15 seats, billed in US dollars. Solo coaches pay $19/month (about AED 70).' },
      { q: 'Does the member app work in Arabic?', a: 'Yes. Members can switch the Trainzilla client app to Arabic or 10 other languages.' },
      { q: 'How do members pay?', a: 'Record cash or card payments at the desk with proof, or sell memberships on your gym page where members pay your own payment link and you confirm with one tap.' },
      { q: 'Can I import our existing members?', a: 'Yes. Import members, leads and past sales from CSV, Excel or a Google Sheets link. Columns are matched automatically, you preview before saving, and imports can be undone.' },
    ],
  },
];

// One reciprocal cluster shared by the homepage and the five country pages (every member lists every
// member, incl. itself), so Google doesn't drop the pairs for a missing return link.
export const COUNTRY_ALTERNATES = [
  { hrefLang: 'en', href: 'https://trainzilla.app/' },
  ...COUNTRY_PAGES.map((c) => ({ hrefLang: c.hrefLang, href: `https://trainzilla.app${c.path}` })),
  { hrefLang: 'x-default', href: 'https://trainzilla.app/' },
];

export function getCountryPage(code: CountryCode): CountryPage {
  const page = COUNTRY_PAGES.find((c) => c.code === code);
  if (!page) throw new Error(`Unknown country page ${code}`);
  return page;
}
